import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  DatastoreCollection,
  FreelancerReputation,
  PortfolioItem,
  ProfileViewUser,
  TopSkill,
} from '@freelancer/datastore';
import { ModalService } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { HeadingSize } from '@freelancer/ui/heading';
import { IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { ModalSize } from '@freelancer/ui/modal';
import { FontColor, FontSize, FontWeight } from '@freelancer/ui/text';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { HireMeWebappModalComponent } from 'app/hire-me/hire-me-webapp-modal/hire-me-webapp-modal.component';
import { HireMeUser } from 'app/hire-me/hire-me.component';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  template: `
    <fl-grid
      *ngIf="(portfolioItem$ | async) as portfolioItem; else loading"
      flTrackingSection="UserPortfolioModal"
      [vAlign]="VerticalAlignment.VERTICAL_STRETCH"
    >
      <fl-col
        class="ItemColumn"
        [col]="8"
        [ngSwitch]="portfolioItem.contentType"
      >
        <app-article-item
          *ngSwitchCase="'article'"
          [portfolioItem]="portfolioItem"
        ></app-article-item>
        <app-picture-item
          *ngSwitchCase="'picture'"
          [portfolioItem]="portfolioItem"
        ></app-picture-item>
        <app-video-item
          *ngSwitchCase="'video'"
          [portfolioItem]="portfolioItem"
        ></app-video-item>
        <app-audio-item
          *ngSwitchCase="'audio'"
          [portfolioItem]="portfolioItem"
        ></app-audio-item>
        <app-code-item
          *ngSwitchCase="'code'"
          [portfolioItem]="portfolioItem"
        ></app-code-item>
        <app-other-item
          *ngSwitchDefault
          [portfolioItem]="portfolioItem"
        ></app-other-item>
      </fl-col>
      <fl-col [col]="4" class="SideUserColumn">
        <fl-bit class="SideUserDetails">
          <app-user-detail
            [user]="user$ | async"
            [showLocation]="false"
            [showTagline]="false"
            [avatarSize]="AvatarSize.MID"
            [ratingSize]="IconSize.SMALL"
            [headingSize]="HeadingSize.XXSMALL"
            [headingSizeTablet]="HeadingSize.XSMALL"
            [flMarginBottom]="Margin.MID"
            [reputation]="reputation$ | async"
            [isOnline]="isOnline$ | async"
          >
          </app-user-detail>
          <fl-button
            class="HiremeButton"
            i18n="User Portfolio sidebar Hire Me"
            flTrackingLabel="PortfolioItemPreviewHireMeButton"
            [flMarginBottom]="Margin.MID"
            [color]="ButtonColor.SECONDARY"
            [size]="ButtonSize.LARGE"
            (click)="hiremeAction()"
          >
            Hire Me
          </fl-button>
          <fl-bit [flMarginBottom]="Margin.MID">
            <fl-text
              i18n="About title label"
              [size]="FontSize.MID"
              [color]="FontColor.DARK"
              [weight]="FontWeight.BOLD"
              [flMarginBottom]="Margin.XSMALL"
            >
              About the project
            </fl-text>
            <fl-text
              class="SideUserDetails-projectDesc"
              i18n="About content label"
              [size]="FontSize.MID"
              [readMore]="250"
            >
              {{ portfolioItem.description }}
            </fl-text>
          </fl-bit>
          <fl-bit
            *ngIf="(itemSkills$ | async) as itemSkills"
            [flMarginBottom]="Margin.MID"
          >
            <fl-text
              class="SideUserDetails-skills-title"
              i18n="Skill title label"
              [size]="FontSize.SMALL"
              [color]="FontColor.MID"
              [flMarginBottom]="Margin.XSMALL"
            >
              Skills
            </fl-text>
            <fl-bit class="SideUserDetails-skills-item">
              <fl-button
                i18n="Skill name label"
                flTrackingLabel="SkillNavigate"
                *ngFor="let skill of itemSkills"
                [color]="ButtonColor.TRANSPARENT_DARK"
                [flMarginRight]="Margin.XXSMALL"
                [flMarginBottom]="Margin.XXSMALL"
                [size]="ButtonSize.SMALL"
                [link]="'/freelancers/skills/' + skill.seoUrl"
              >
                {{ skill.name }}
              </fl-button>
            </fl-bit>
          </fl-bit>
          <fl-hr></fl-hr>
        </fl-bit>
      </fl-col>
    </fl-grid>
    <ng-template #loading>
      <fl-grid>
        <fl-col [col]="8">
          <fl-loading-text [rows]="1" [padded]="false"></fl-loading-text>
        </fl-col>
        <fl-col [col]="4">
          <fl-loading-text
            *ngFor="let n of [0, 1, 2, 3]"
            [rows]="1"
            [padded]="false"
          ></fl-loading-text>
        </fl-col>
      </fl-grid>
    </ng-template>
  `,
  styleUrls: ['./user-portfolio-item-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPortfolioItemOverlayComponent implements OnInit {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  FontSize = FontSize;
  FontWeight = FontWeight;
  HeadingSize = HeadingSize;
  IconSize = IconSize;
  Margin = Margin;
  VerticalAlignment = VerticalAlignment;

  @Input() hireMeUser$: Rx.Observable<HireMeUser>;
  @Input() isOnline$: Rx.Observable<boolean>;
  @Input() portfolioItem$: Rx.Observable<PortfolioItem>;
  @Input() reputation$: Rx.Observable<FreelancerReputation>;
  @Input() skillsCollection: DatastoreCollection<'topSkills'>;
  @Input() user$: Rx.Observable<ProfileViewUser>;

  itemSkills$: Rx.Observable<ReadonlyArray<TopSkill>>;

  constructor(private modalService: ModalService) {}

  ngOnInit() {
    this.itemSkills$ = Rx.combineLatest(
      this.skillsCollection.valueChanges(),
      this.portfolioItem$,
    ).pipe(
      map(([skills, item]) => skills.filter(s => item.jobs.includes(s.id))),
    );
  }

  hiremeAction() {
    this.modalService.close();
    this.modalService.open(HireMeWebappModalComponent, {
      inputs: {
        hireMeUser$: this.hireMeUser$,
      },
      size: ModalSize.XSMALL,
      edgeToEdge: true,
    });
  }
}
